import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { insuranceAPI, billingAPI } from '../../lib/api';
import { 
  CreditCard, 
  Shield, 
  Plus,
  Check,
  X,
  Warning,
  Receipt,
  Wallet,
  Clock,
  CheckCircle,
  ArrowSquareOut,
  Spinner
} from '@phosphor-icons/react';

const insuranceProviders = [
  'Aetna', 'Blue Cross Blue Shield', 'Cigna', 'Humana', 'Kaiser Permanente',
  'UnitedHealthcare', 'Anthem', 'Molina Healthcare', 'Other'
];

const relationships = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'other', label: 'Other' },
];

export default function InsuranceBillingPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [insurance, setInsurance] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [showAddInsurance, setShowAddInsurance] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');
  
  const [insuranceForm, setInsuranceForm] = useState({
    insurance_provider: '',
    policy_number: '',
    group_number: '',
    subscriber_name: '',
    subscriber_dob: '',
    relationship_to_subscriber: 'self',
    coverage_type: 'medical',
    copay_amount: '',
    deductible: '',
    effective_date: '',
    expiration_date: '',
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'credit_card',
    reference_number: '',
  });

  const handlePaymentCallback = useCallback(async (sessionId) => {
    setProcessingPayment(true);
    try {
      const res = await billingAPI.getPaymentStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        setPaymentMessage('Payment successful! Thank you.');
        loadData();
      } else {
        setPaymentMessage('Payment is being processed. Please refresh later.');
      }
    } catch (error) {
      setPaymentMessage('Error checking payment status.');
    } finally {
      setProcessingPayment(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Check for payment callback
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (payment === 'success' && sessionId) {
      handlePaymentCallback(sessionId);
    } else if (payment === 'cancelled') {
      setPaymentMessage('Payment was cancelled.');
    }
  }, [searchParams, handlePaymentCallback]);

  const loadData = async () => {
    try {
      const [insuranceRes, invoicesRes, summaryRes] = await Promise.all([
        insuranceAPI.getMy(),
        billingAPI.getInvoices(),
        billingAPI.getSummary(),
      ]);
      setInsurance(insuranceRes.data);
      setInvoices(invoicesRes.data);
      setBillingSummary(summaryRes.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async (invoice) => {
    setProcessingPayment(true);
    try {
      const res = await billingAPI.createCheckout(invoice.id);
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error creating checkout:', error);
      setPaymentMessage('Failed to initiate payment. Please try again.');
      setProcessingPayment(false);
    }
  };

  const handleAddInsurance = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...insuranceForm,
        copay_amount: insuranceForm.copay_amount ? parseFloat(insuranceForm.copay_amount) : null,
        deductible: insuranceForm.deductible ? parseFloat(insuranceForm.deductible) : null,
      };
      await insuranceAPI.add(data);
      setShowAddInsurance(false);
      setInsuranceForm({
        insurance_provider: '', policy_number: '', group_number: '', subscriber_name: '',
        subscriber_dob: '', relationship_to_subscriber: 'self', coverage_type: 'medical',
        copay_amount: '', deductible: '', effective_date: '', expiration_date: ''
      });
      loadData();
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error adding insurance:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!showPayment) return;
    
    setSaving(true);
    try {
      await billingAPI.recordPayment({
        invoice_id: showPayment.id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null,
      });
      setShowPayment(null);
      setPaymentForm({ amount: '', payment_method: 'credit_card', reference_number: '' });
      loadData();
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error processing payment:', error);
    } finally {
      setSaving(false);
    }
  };

  const getVerificationStatusBadge = (status) => {
    switch (status) {
      case 'verified': return 'status-completed';
      case 'pending': return 'status-pending';
      case 'denied': return 'status-urgent';
      case 'expired': return 'bg-slate-100 text-slate-600';
      default: return 'status-pending';
    }
  };

  const getInvoiceStatusBadge = (status) => {
    switch (status) {
      case 'paid': return 'status-completed';
      case 'partially_paid': return 'status-in-progress';
      case 'pending': case 'submitted': return 'status-pending';
      case 'denied': return 'status-urgent';
      default: return 'status-pending';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Insurance & Billing
        </h1>
        <p className="text-slate-500 mt-1">Manage your insurance information and view billing</p>
      </div>

      {/* Payment Success/Error Message */}
      {paymentMessage && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
          paymentSuccess 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-amber-50 border border-amber-200 text-amber-700'
        }`} data-testid="payment-message">
          {paymentSuccess ? (
            <CheckCircle size={24} weight="fill" className="text-green-500" />
          ) : (
            <Warning size={24} className="text-amber-500" />
          )}
          <span>{paymentMessage}</span>
          <button 
            onClick={() => { setPaymentMessage(''); setPaymentSuccess(false); }}
            className="ml-auto text-slate-400 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      {billingSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="kpi-card card-hover" data-testid="billing-total-billed">
            <div className="flex items-center justify-between">
              <div>
                <div className="kpi-value">${billingSummary.total_billed.toFixed(2)}</div>
                <div className="kpi-label">Total Billed</div>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Receipt size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="kpi-card card-hover" data-testid="billing-collected">
            <div className="flex items-center justify-between">
              <div>
                <div className="kpi-value">${billingSummary.total_collected.toFixed(2)}</div>
                <div className="kpi-label">Paid</div>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="kpi-card card-hover" data-testid="billing-outstanding">
            <div className="flex items-center justify-between">
              <div>
                <div className="kpi-value text-amber-600">${billingSummary.total_outstanding.toFixed(2)}</div>
                <div className="kpi-label">Outstanding</div>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Wallet size={24} className="text-amber-600" />
              </div>
            </div>
          </div>

          <div className="kpi-card card-hover" data-testid="billing-invoices">
            <div className="flex items-center justify-between">
              <div>
                <div className="kpi-value">{billingSummary.invoice_count}</div>
                <div className="kpi-label">Invoices</div>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <CreditCard size={24} className="text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Insurance Section */}
        <div className="card-clinical">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Shield size={18} /> Insurance Information
              </h2>
              <button
                onClick={() => setShowAddInsurance(true)}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
                data-testid="add-insurance-btn"
              >
                <Plus size={16} /> Add Insurance
              </button>
            </div>
          </div>
          
          {insurance.length === 0 ? (
            <div className="p-12 text-center">
              <Shield size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No insurance on file</p>
              <button
                onClick={() => setShowAddInsurance(true)}
                className="btn-teal"
              >
                Add Insurance
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {insurance.map((ins) => (
                <div key={ins.id} className="p-4" data-testid={`insurance-${ins.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-slate-900">{ins.insurance_provider}</div>
                      <div className="text-sm text-slate-500 mt-1">
                        Policy: {ins.policy_number}
                        {ins.group_number && ` | Group: ${ins.group_number}`}
                      </div>
                      <div className="text-sm text-slate-500">
                        Subscriber: {ins.subscriber_name} ({ins.relationship_to_subscriber})
                      </div>
                      {ins.copay_amount && (
                        <div className="text-sm text-slate-600 mt-2">
                          Copay: ${ins.copay_amount} | Deductible: ${ins.deductible || 0}
                        </div>
                      )}
                    </div>
                    <span className={`status-badge ${getVerificationStatusBadge(ins.verification_status)}`}>
                      {ins.verification_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invoices Section */}
        <div className="card-clinical">
          <div className="p-6 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Receipt size={18} /> Invoices & Payments
            </h2>
          </div>
          
          {invoices.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No invoices yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {invoices.map((inv) => (
                <div key={inv.id} className="p-4" data-testid={`invoice-${inv.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium text-slate-900">
                        Invoice #{inv.id.slice(0, 8)}
                      </div>
                      <div className="text-sm text-slate-500">
                        {new Date(inv.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`status-badge ${getInvoiceStatusBadge(inv.status)}`}>
                      {inv.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <span className="text-slate-500">Total:</span>
                      <span className="ml-2 font-medium">${inv.subtotal.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Insurance:</span>
                      <span className="ml-2 text-green-600">-${inv.insurance_adjustment.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">You Owe:</span>
                      <span className="ml-2 font-medium">${inv.patient_responsibility.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Balance:</span>
                      <span className={`ml-2 font-medium ${inv.balance_due > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        ${inv.balance_due.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {inv.balance_due > 0 && (
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => handleStripePayment(inv)}
                        disabled={processingPayment}
                        className="flex-1 btn-teal text-sm py-2 flex items-center justify-center gap-2"
                        data-testid={`pay-invoice-stripe-${inv.id}`}
                      >
                        {processingPayment ? (
                          <div className="spinner" />
                        ) : (
                          <>
                            <CreditCard size={16} />
                            Pay with Card
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowPayment(inv);
                          setPaymentForm({ ...paymentForm, amount: inv.balance_due.toFixed(2) });
                        }}
                        className="btn-outline text-sm py-2"
                        data-testid={`pay-invoice-manual-${inv.id}`}
                      >
                        Other
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Insurance Modal */}
      {showAddInsurance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="add-insurance-modal">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Add Insurance</h2>
                <button onClick={() => setShowAddInsurance(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleAddInsurance} className="p-6 space-y-4">
              <div>
                <label className="label-clinical">Insurance Provider</label>
                <select
                  value={insuranceForm.insurance_provider}
                  onChange={(e) => setInsuranceForm({ ...insuranceForm, insurance_provider: e.target.value })}
                  className="input-clinical"
                  required
                  data-testid="insurance-provider"
                >
                  <option value="">Select provider</option>
                  {insuranceProviders.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Policy Number</label>
                  <input
                    type="text"
                    value={insuranceForm.policy_number}
                    onChange={(e) => setInsuranceForm({ ...insuranceForm, policy_number: e.target.value })}
                    className="input-clinical"
                    required
                    data-testid="policy-number"
                  />
                </div>
                <div>
                  <label className="label-clinical">Group Number</label>
                  <input
                    type="text"
                    value={insuranceForm.group_number}
                    onChange={(e) => setInsuranceForm({ ...insuranceForm, group_number: e.target.value })}
                    className="input-clinical"
                    data-testid="group-number"
                  />
                </div>
              </div>

              <div>
                <label className="label-clinical">Subscriber Name</label>
                <input
                  type="text"
                  value={insuranceForm.subscriber_name}
                  onChange={(e) => setInsuranceForm({ ...insuranceForm, subscriber_name: e.target.value })}
                  className="input-clinical"
                  required
                  data-testid="subscriber-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Subscriber DOB</label>
                  <input
                    type="date"
                    value={insuranceForm.subscriber_dob}
                    onChange={(e) => setInsuranceForm({ ...insuranceForm, subscriber_dob: e.target.value })}
                    className="input-clinical"
                    required
                    data-testid="subscriber-dob"
                  />
                </div>
                <div>
                  <label className="label-clinical">Relationship</label>
                  <select
                    value={insuranceForm.relationship_to_subscriber}
                    onChange={(e) => setInsuranceForm({ ...insuranceForm, relationship_to_subscriber: e.target.value })}
                    className="input-clinical"
                    data-testid="relationship"
                  >
                    {relationships.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Copay Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={insuranceForm.copay_amount}
                    onChange={(e) => setInsuranceForm({ ...insuranceForm, copay_amount: e.target.value })}
                    className="input-clinical"
                    placeholder="25.00"
                    data-testid="copay-amount"
                  />
                </div>
                <div>
                  <label className="label-clinical">Deductible ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={insuranceForm.deductible}
                    onChange={(e) => setInsuranceForm({ ...insuranceForm, deductible: e.target.value })}
                    className="input-clinical"
                    placeholder="1000.00"
                    data-testid="deductible"
                  />
                </div>
              </div>

              <div>
                <label className="label-clinical">Effective Date</label>
                <input
                  type="date"
                  value={insuranceForm.effective_date}
                  onChange={(e) => setInsuranceForm({ ...insuranceForm, effective_date: e.target.value })}
                  className="input-clinical"
                  required
                  data-testid="effective-date"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowAddInsurance(false)} className="flex-1 btn-outline">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 btn-teal" data-testid="save-insurance-btn">
                  {saving ? <div className="spinner mx-auto" /> : 'Save Insurance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full" data-testid="payment-modal">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Make Payment</h2>
                <button onClick={() => setShowPayment(null)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <form onSubmit={handlePayment} className="p-6 space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-500">Balance Due</div>
                <div className="text-2xl font-bold text-slate-900">${showPayment.balance_due.toFixed(2)}</div>
              </div>

              <div>
                <label className="label-clinical">Payment Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="input-clinical"
                  max={showPayment.balance_due}
                  required
                  data-testid="payment-amount"
                />
              </div>

              <div>
                <label className="label-clinical">Payment Method</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  className="input-clinical"
                  data-testid="payment-method"
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowPayment(null)} className="flex-1 btn-outline">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 btn-teal" data-testid="submit-payment-btn">
                  {saving ? <div className="spinner mx-auto" /> : 'Submit Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
